import React, { useEffect, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import HoverableText from '#@/components/HoverableText';
import { COLOR } from '#@/shared/utils/styles';

import Icon from '../Icon';
import IconInteractable from '../IconInteractable';
import { Tooltip } from '../Tooltip';
import { CheckboxIcon, CheckboxWrapper, Container, Label, StyledCheckbox } from './Styles';

export interface CheckboxProps {
  className?: string;
  checkboxLabel?: React.ReactNode | string;
  name?: string;
  value?: string | boolean;
  checked?: boolean;
  indeterminate?: boolean;
  invalid?: boolean;
  truncate?: boolean;
  disabled?: boolean;
  onChange: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  tooltipHint?: string;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      checkboxLabel,
      onChange,
      invalid = false,
      value,
      checked,
      indeterminate = false,
      disabled = false,
      tooltipHint,
      truncate = false,
      ...checkboxProps
    },
    ref,
  ) => {
    const localRef = useRef<HTMLInputElement>(null);

    const isChecked = () => {
      if (indeterminate) return false; // Submitting an indeterminate checkbox is the same as unchecked.
      if (checked !== undefined) return checked;
      return Boolean(value);
    };

    useEffect(() => {
      if (!localRef.current) return;

      // This attribute lets us use the `:indeterminate` CSS pseudo-class, but can
      // only be set imperatively
      localRef.current.indeterminate = indeterminate;
    });

    const renderIcon = () => {
      if (indeterminate) return <CheckboxIcon type="minus-fat" $disabled={disabled} />;
      if (isChecked()) return <CheckboxIcon type="check-fat" $disabled={disabled} />;
      return null;
    };

    const renderLabel = () => {
      if (!checkboxLabel) return null;
      return truncate ? <HoverableText text={checkboxLabel} /> : <span>{checkboxLabel}</span>;
    };

    return (
      <Container className={className}>
        <Label $invalid={invalid} $disabled={disabled} data-cy-checkbox data-checkbox>
          <CheckboxWrapper>
            <StyledCheckbox
              {...checkboxProps}
              ref={mergeRefs([ref, localRef])}
              value={String(value)}
              disabled={disabled}
              checked={isChecked()}
              onChange={(event) => onChange(event.target.checked, event)}
              data-indeterminate={indeterminate ? true : undefined}
            />
            {renderIcon()}
          </CheckboxWrapper>
          {renderLabel()}
        </Label>
        {tooltipHint && (
          <Tooltip<HTMLSpanElement> isTriggerElementClickable={false} bodyText={tooltipHint}>
            {({ ref: triggerRef, getReferenceProps }) => (
              <IconInteractable ref={triggerRef} {...getReferenceProps()}>
                <Icon color={COLOR.textSecondary} type="info" />
              </IconInteractable>
            )}
          </Tooltip>
        )}
      </Container>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
